/**
 * Site-wide footer.
 *
 * Renders the same gold-hairline treatment used on the auth pages so
 * every logged-in surface has a consistent anchor at the bottom.
 *
 * Used by SiteLayout. Drop-in for any page that needs the brand
 * footer outside the SiteLayout (e.g. LandingPage).
 */

import { Box, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

const GOLD = '#bb8c3f';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

export function Footer() {
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        py: { xs: 2, md: 2.5 },
        px: { xs: 2, sm: 4, md: 6 },
        borderTop: '1px solid rgba(15, 10, 7, 0.06)',
        bgcolor: 'transparent',
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: 1400 }}>
        <Box
          sx={{
            display: 'flex',
            // Stacks on a phone. As one row it was ~453px of
            // unshrinkable content in a 360px viewport, so every page
            // that renders the footer scrolled sideways.
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            // The links are a second unshrinkable item now that there
            // are two of them, so the row is allowed to wrap rather
            // than push the page sideways at tablet widths.
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: { xs: 1, sm: 2 },
          }}
        >
          <Box
            aria-hidden
            sx={{
              // Decoration only — the first thing to go when there
              // isn't room for the words themselves.
              display: { xs: 'none', sm: 'block' },
              flex: '0 0 32px',
              height: '1px',
              background: `linear-gradient(to right, transparent, ${GOLD})`,
              opacity: 0.55,
            }}
          />
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 9.5,
              fontWeight: 500,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'rgba(15, 10, 7, 0.4)',
              // Free to wrap on a phone, pinned on a real row.
              flexShrink: { xs: 1, sm: 0 },
              textAlign: 'center',
            }}
          >
            © {year} Hooten · Young · Internal use only
          </Typography>
          <Box
            aria-hidden
            sx={{
              display: { xs: 'none', sm: 'block' },
              flex: 1,
              height: '1px',
              background: `linear-gradient(to right, ${GOLD}, transparent)`,
              opacity: 0.55,
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Box
              component={Link}
              to="/user-guide"
              sx={{
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 32,
                px: 1,
                fontFamily: FONT_INTER,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'rgba(15, 10, 7, 0.5)',
                textDecoration: 'none',
                transition: 'color 0.15s',
                '&:hover': { color: GOLD },
              }}
            >
              User Guide
            </Box>
            <Box
              component={Link}
              to="/whats-new"
              sx={{
                flexShrink: 0,
                // 9.5px text is a 15px-tall tap target; padding brings it
                // to something a thumb can actually hit.
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 32,
                px: 1,
                fontFamily: FONT_INTER,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'rgba(15, 10, 7, 0.5)',
                textDecoration: 'none',
                transition: 'color 0.15s',
                '&:hover': { color: GOLD },
              }}
            >
              What&apos;s New
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
